import { useEffect, useRef, useState } from 'react';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import './RichText.css';

/** Escape text destined for element content (link label). */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/** Escape a value destined for a double-quoted attribute (href). */
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

/**
 * A small, dependency-free WYSIWYG editor + matching renderer.
 *
 * The editor is a contentEditable div driven by document.execCommand. It is
 * deliberately uncontrolled at the DOM level: we seed innerHTML ONCE on mount
 * from `value` and never write innerHTML again on re-render, because re-setting
 * it would collapse the caret to the start on every keystroke. We push changes
 * UP via onChange(sanitizeHtml(...)) on input/blur; the parent stores the
 * sanitized HTML but doesn't feed it back into the DOM.
 */

interface EditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

type Cmd =
  | { kind: 'cmd'; command: string; label: string; title: string }
  | { kind: 'block'; tag: string; label: string; title: string }
  | { kind: 'link'; label: string; title: string }
  | { kind: 'clear'; label: string; title: string };

const TOOLBAR: Cmd[] = [
  { kind: 'cmd', command: 'bold', label: 'B', title: 'Bold' },
  { kind: 'cmd', command: 'italic', label: 'I', title: 'Italic' },
  { kind: 'cmd', command: 'underline', label: 'U', title: 'Underline' },
  { kind: 'block', tag: 'h2', label: 'H2', title: 'Heading' },
  { kind: 'block', tag: 'h3', label: 'H3', title: 'Subheading' },
  { kind: 'cmd', command: 'insertUnorderedList', label: '• List', title: 'Bullet list' },
  { kind: 'cmd', command: 'insertOrderedList', label: '1. List', title: 'Numbered list' },
  { kind: 'link', label: 'Link', title: 'Insert link' },
  { kind: 'clear', label: 'Clear', title: 'Clear formatting' },
];

const QUERYABLE = new Set(['bold', 'italic', 'underline', 'insertUnorderedList', 'insertOrderedList']);

export function RichTextEditor({ value, onChange, disabled, placeholder }: EditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Inline link popover: capture link text + URL instead of a bare prompt, so
  // authors can give a link a label distinct from its href.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  // The selection is lost once focus moves into the popover inputs, so we
  // snapshot it on open and restore it before inserting.
  const savedRange = useRef<Range | null>(null);

  // Seed once on mount — never re-write innerHTML on subsequent renders.
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = sanitizeHtml(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function emit() {
    if (ref.current) onChange(sanitizeHtml(ref.current.innerHTML));
  }

  function exec(command: string, arg?: string) {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  }

  function applyBlock(tag: string) {
    if (disabled) return;
    ref.current?.focus();
    // Toggle: if already in this block, drop back to a paragraph.
    const current = (document.queryCommandValue('formatBlock') || '').toLowerCase();
    document.execCommand('formatBlock', false, current === tag ? 'p' : tag);
    emit();
  }

  function openLinkDialog() {
    if (disabled) return;
    const sel = window.getSelection();
    savedRange.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    // Pre-fill the label from any selected text so wrapping a selection still works.
    setLinkText(savedRange.current ? savedRange.current.toString() : '');
    setLinkUrl('');
    setLinkOpen(true);
  }

  function cancelLink() {
    setLinkOpen(false);
    setLinkText('');
    setLinkUrl('');
    savedRange.current = null;
  }

  function confirmLink() {
    const url = linkUrl.trim();
    if (!url) return;
    if (!/^(https?:\/\/|mailto:)/i.test(url)) {
      window.alert('Links must start with http://, https:// or mailto:');
      return;
    }
    const label = linkText.trim() || url;
    const node = ref.current;
    if (node) {
      node.focus();
      // Restore the snapshotted caret/selection so the anchor lands in place.
      if (savedRange.current) {
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(savedRange.current);
      }
      const anchor = `<a href="${escapeAttr(url)}">${escapeHtml(label)}</a>`;
      document.execCommand('insertHTML', false, anchor);
      emit();
    }
    cancelLink();
  }

  function clearFormatting() {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand('removeFormat');
    document.execCommand('formatBlock', false, 'p');
    emit();
  }

  function isActive(c: Cmd): boolean {
    try {
      if (c.kind === 'cmd' && QUERYABLE.has(c.command)) return document.queryCommandState(c.command);
      if (c.kind === 'block') return (document.queryCommandValue('formatBlock') || '').toLowerCase() === c.tag;
    } catch {
      /* queryCommand can throw when the selection is detached */
    }
    return false;
  }

  return (
    <div className={`richtext-editor ${disabled ? 'richtext-editor--disabled' : ''}`}>
      <div className="richtext-toolbar" role="toolbar" aria-label="Text formatting">
        {TOOLBAR.map((c) => (
          <button
            key={c.label}
            type="button"
            // Keep focus in the editable region so the command has a selection.
            onMouseDown={(e) => e.preventDefault()}
            className={`richtext-tool ${isActive(c) ? 'richtext-tool--on' : ''}`}
            title={c.title}
            aria-label={c.title}
            disabled={disabled}
            onClick={() => {
              if (c.kind === 'cmd') exec(c.command);
              else if (c.kind === 'block') applyBlock(c.tag);
              else if (c.kind === 'link') openLinkDialog();
              else clearFormatting();
            }}
          >
            {c.label}
          </button>
        ))}
      </div>
      {linkOpen && (
        <div className="richtext-linkbar" role="group" aria-label="Insert link">
          <input
            type="text"
            className="richtext-linkbar__field"
            placeholder="Link text"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            autoFocus
          />
          <input
            type="url"
            className="richtext-linkbar__field"
            placeholder="https:// or mailto:"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); confirmLink(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelLink(); }
            }}
          />
          <button
            type="button"
            className="richtext-linkbar__btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={confirmLink}
            disabled={!linkUrl.trim()}
          >
            Add link
          </button>
          <button
            type="button"
            className="richtext-linkbar__btn richtext-linkbar__btn--ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancelLink}
          >
            Cancel
          </button>
        </div>
      )}
      <div
        ref={ref}
        className="richtext-surface"
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        aria-label="Description"
        data-placeholder={placeholder ?? ''}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
      />
    </div>
  );
}

interface ViewProps {
  html: string | null | undefined;
  className?: string;
}

/** Render sanitized rich-text HTML. Returns null when there's nothing to show. */
export function RichTextView({ html, className }: ViewProps) {
  if (!html) return null;
  const clean = sanitizeHtml(html);
  if (!clean.trim()) return null;
  return (
    <div
      className={className ? `richtext ${className}` : 'richtext'}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
